import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, Loader2, CheckCircle, Clock, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeedbackTab = ({ token }) => {
  const [issues, setIssues] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeedbackHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchFeedbackHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/feedback`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch feedback history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!issues.trim() && !suggestions.trim()) {
      toast.error('Please fill in at least one field');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/feedback`,
        { issues: issues.trim(), suggestions: suggestions.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Thank you for your feedback!');
      setIssues('');
      setSuggestions('');
      fetchFeedbackHistory();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit feedback';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
            <Clock className="w-3 h-3" />
            Pending Review
          </span>
        );
      case 'reviewed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            <CheckCircle className="w-3 h-3" />
            Reviewed
          </span>
        );
      case 'implemented':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <Sparkles className="w-3 h-3" />
            Implemented
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {/* Submit Feedback Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card rounded-2xl border border-border/50 shadow-lg overflow-hidden"
      >
        <div className="p-6 border-b border-border/50 bg-gradient-to-r from-primary/5 to-secondary/5">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-primary" />
            Share Your Feedback
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Help us improve by telling us what's working and what could be better
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Issues Section */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-foreground">
              <AlertCircle className="w-4 h-4 inline mr-2 text-red-500" />
              What doesn't work well?
            </label>
            <Textarea
              data-testid="feedback-issues-input"
              value={issues}
              onChange={(e) => setIssues(e.target.value)}
              placeholder="Tell us about any bugs, glitches, or features that aren't working as expected..."
              className="min-h-[120px] rounded-xl resize-none"
            />
          </div>

          {/* Suggestions Section */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-foreground">
              <Sparkles className="w-4 h-4 inline mr-2 text-amber-500" />
              What changes would you like to see?
            </label>
            <Textarea
              data-testid="feedback-suggestions-input"
              value={suggestions}
              onChange={(e) => setSuggestions(e.target.value)}
              placeholder="Share your ideas for new features, improvements, or changes you'd like to see..."
              className="min-h-[120px] rounded-xl resize-none"
            />
          </div>

          <Button
            data-testid="submit-feedback-button"
            type="submit"
            disabled={submitting || (!issues.trim() && !suggestions.trim())}
            className="w-full rounded-full py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all bg-gradient-to-r from-primary to-[#8FD14F]"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit Feedback
              </>
            )}
          </Button>
        </form>
      </motion.div>

      {/* Feedback History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl border border-border/50 shadow-lg overflow-hidden"
      >
        <div className="p-6 border-b border-border/50">
          <h2 className="text-xl font-bold">Your Feedback History</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Track the status of your submitted feedback
          </p>
        </div>

        <div className="divide-y divide-border/50">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
              <p className="mt-2 text-muted-foreground">Loading feedback...</p>
            </div>
          ) : feedbackHistory.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>You haven't submitted any feedback yet</p>
              <p className="text-sm mt-1">Share your thoughts using the form above!</p>
            </div>
          ) : (
            <AnimatePresence>
              {feedbackHistory.map((feedback, index) => (
                <motion.div
                  key={feedback.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-6 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="text-xs text-muted-foreground">
                      {new Date(feedback.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    {getStatusBadge(feedback.status)}
                  </div>

                  {feedback.issues && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-red-500 uppercase mb-1">Issues Reported</p>
                      <p className="text-sm text-foreground whitespace-pre-line">{feedback.issues}</p>
                    </div>
                  )}

                  {feedback.suggestions && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-amber-500 uppercase mb-1">Suggestions</p>
                      <p className="text-sm text-foreground whitespace-pre-line">{feedback.suggestions}</p>
                    </div>
                  )}

                  {feedback.admin_response && (
                    <div className="mt-3 p-3 bg-primary/5 rounded-xl border-l-4 border-primary">
                      <p className="text-xs font-semibold text-primary uppercase mb-1">Admin Response</p>
                      <p className="text-sm text-foreground">{feedback.admin_response}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default FeedbackTab;
